import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Logo, Check } from '../components/Icons.jsx';
import { Intro, Done, mainStyle } from './VennArchitect.jsx';
import { TutorNudge } from '../components/TutorCard.jsx';
import { tutorFor } from '../lib/tutors.js';
import DrillResultBar from '../components/DrillResultBar.jsx';
import StuckPopIn from '../components/StuckPopIn.jsx';

// Question generator. Each returns { q, answer, working, kind }.
const types = {
  currency: (level) => {
    const rate = level === 'easy' ? 1.2 : (1 + Math.random() * 0.6);
    const amount = level === 'easy' ? 10 * (1 + Math.floor(Math.random() * 10)) : 1 + Math.floor(Math.random() * 500);
    const answer = Math.round(amount * rate * 100) / 100;
    return { q: `£${amount} at £1 = $${rate.toFixed(2)}. How many dollars?`, answer, working: `${amount} × ${rate.toFixed(2)} = ${answer.toFixed(2)}`, kind: 'currency', acceptDelta: 0.05 };
  },
  units: (level) => {
    if (level === 'easy') {
      const km = 1 + Math.floor(Math.random() * 20);
      return { q: `${km} km in metres?`, answer: km * 1000, working: `${km} × 1000 = ${km * 1000}`, kind: 'units' };
    }
    const miles = 5 + Math.floor(Math.random() * 95);
    const answer = Math.round(miles * 1.609 * 10) / 10;
    return { q: `${miles} miles in km (1 mile = 1.609 km)?`, answer, working: `${miles} × 1.609 = ${answer}`, kind: 'units', acceptDelta: 0.1 };
  },
  percent: (level) => {
    const base = level === 'easy' ? 20 + Math.floor(Math.random() * 80) : 50 + Math.floor(Math.random() * 950);
    const pct = level === 'easy' ? 10 * (1 + Math.floor(Math.random() * 9)) : 5 + Math.floor(Math.random() * 90);
    const answer = Math.round(base * pct) / 100;
    return { q: `${pct}% of ${base}?`, answer, working: `${pct}/100 × ${base} = ${answer}`, kind: 'percent', acceptDelta: 0.05 };
  },
  speed: (level) => {
    if (level === 'easy') {
      const kmh = 30 + Math.floor(Math.random() * 50);
      const hours = 1 + Math.floor(Math.random() * 4);
      return { q: `A car travels at ${kmh} km/h for ${hours}h. Distance?`, answer: kmh * hours, working: `${kmh} × ${hours} = ${kmh * hours} km`, kind: 'speed' };
    }
    const dist = 20 + Math.floor(Math.random() * 180);
    const kmh = 30 + Math.floor(Math.random() * 80);
    const minutes = Math.round((dist / kmh) * 60);
    return { q: `${dist} km at ${kmh} km/h. Time in minutes?`, answer: minutes, working: `${dist}/${kmh} h = ${(dist / kmh).toFixed(2)}h × 60 ≈ ${minutes} min`, kind: 'speed', acceptDelta: 1 };
  }
};

function makeQuestion(level) {
  const keys = Object.keys(types);
  const t = keys[Math.floor(Math.random() * keys.length)];
  return types[t](level);
}

export default function ConversionGauntlet() {
  const [stage, setStage] = useState('intro');
  const [level, setLevel] = useState('easy');
  const [ucatsen, setUcatsen] = useState(false);
  const [q, setQ] = useState(null);
  const [input, setInput] = useState('');
  const [score, setScore] = useState({ correct: 0, wrong: 0, attempts: 0 });
  const [wrongStreak, setWrongStreak] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(120);
  const [flash, setFlash] = useState(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  const duration = ucatsen ? 150 : 120;

  useEffect(() => {
    if (stage !== 'drill') return;
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { clearInterval(timerRef.current); setStage('done'); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [stage]);

  const start = () => {
    setScore({ correct: 0, wrong: 0, attempts: 0 });
    setSecondsLeft(duration);
    setInput(''); setFlash(null);
    setQ(makeQuestion(level));
    setStage('drill');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const submit = () => {
    if (!q || input.trim() === '') return;
    const guess = Number(input);
    const delta = q.acceptDelta || 0;
    const ok = Math.abs(guess - q.answer) <= delta;
    setScore((s) => ({ correct: s.correct + (ok ? 1 : 0), wrong: s.wrong + (ok ? 0 : 1), attempts: s.attempts + 1 }));
    setWrongStreak((w) => ok ? 0 : w + 1);
    setFlash({ ok, msg: ok ? 'Correct' : `Was ${q.answer}` });
    setInput('');
    setQ(makeQuestion(level));
    setTimeout(() => setFlash(null), 1100);
    inputRef.current?.focus();
  };

  const accuracy = score.attempts > 0 ? Math.round((score.correct / score.attempts) * 100) : 0;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: '#fff', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 10 }}>
        <Link to="/practice" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-2)', textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>
          <ArrowLeft width="16" height="16" />Exit
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo width="26" height="26" onLight />
          <strong style={{ fontSize: 14, color: 'var(--brand-deep)', fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.04em' }}>Conversion gauntlet</strong>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: secondsLeft < 20 ? '#b91c1c' : 'var(--text-2)', minWidth: 60, textAlign: 'right' }}>
          {stage === 'drill' && `${secondsLeft}s`}
        </div>
      </header>

      {stage === 'intro' && (
        <Intro
          subtest="QR" title="Conversion gauntlet"
          body="Currency, units, percentages and speed/distance/time — the highest-volume shapes in UCAT QR. Quick-fire."
          how={['Pick easy or UCAT-style', 'Type your answer, Enter to submit', 'No calculator. 120 seconds standard / 150 with UCATSEN']}
          difficulty={level} setDifficulty={setLevel}
          difficultyOptions={[
            { v: 'easy', label: 'Foundation', sub: 'Round numbers, simple rates' },
            { v: 'hard', label: 'UCAT-style', sub: 'Awkward decimals, mixed units' }
          ]}
          ucatsen={ucatsen} setUcatsen={setUcatsen}
          onStart={start}
        />
      )}

      {stage === 'drill' && <StuckPopIn wrongStreak={wrongStreak} slug="conversion-gauntlet" sectionLabel="conversion gauntlet" />}
      {stage === 'drill' && q && (
        <main style={mainStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 18 }}>
            <span style={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{q.kind}</span>
            <span><strong style={{ color: 'var(--live-green)' }}>{score.correct}</strong> correct · <strong style={{ color: '#b91c1c' }}>{score.wrong}</strong> wrong</span>
          </div>

          <div style={{
            background: flash?.ok === true ? '#e7f7ef' : flash?.ok === false ? '#fee2e2' : '#fff',
            border: `2px solid ${flash?.ok === true ? 'var(--live-green)' : flash?.ok === false ? '#b91c1c' : 'var(--border)'}`,
            borderRadius: 16, padding: 'clamp(20px, 4vw, 36px)', textAlign: 'center', marginBottom: 16, minHeight: 130,
            transition: 'all 150ms ease',
            display: 'flex', flexDirection: 'column', justifyContent: 'center'
          }}>
            <div style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 700, fontFamily: 'Montserrat, sans-serif', color: 'var(--brand-deep)' }}>{q.q}</div>
            {flash && (
              <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600, color: flash.ok ? 'var(--live-green)' : '#b91c1c' }}>{flash.msg}</div>
            )}
          </div>

          <input
            ref={inputRef}
            type="number" step="any" inputMode="decimal"
            value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            placeholder="Your answer"
            style={{ width: '100%', padding: '16px 18px', fontSize: 22, fontWeight: 700, textAlign: 'center', border: '2px solid var(--border-strong)', borderRadius: 14, fontFamily: 'Montserrat, sans-serif' }}
          />
          <div style={{ marginTop: 14, display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button onClick={submit} className="btn btn-primary">Submit <Check width="14" height="14" /></button>
            <button onClick={() => setQ(makeQuestion(level))} className="btn btn-secondary">Skip <ArrowRight width="14" height="14" /></button>
          </div>
        </main>
      )}

      {stage === 'done' && (
        <Done subtest="QR" title="Conversion gauntlet" stats={[
          { label: 'Correct', value: score.correct },
          { label: 'Wrong', value: score.wrong },
          { label: 'Accuracy', value: `${accuracy}%` }
        ]} onRestart={start}
          extra={<>
            <DrillResultBar slug="conversion-gauntlet" score={score.correct} accuracyPct={accuracy} metadata={{ level, ucatsen }} />
            {accuracy < 65 && score.attempts >= 5 && (
              <TutorNudge tutor={tutorFor('conversion')} reason="Conversions are the most-failed QR shape." kicker="Want this fixed faster?" />
            )}
          </>}
        />
      )}
    </div>
  );
}
